import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  getAnalyticsSummary, 
  getLeadFunnel, 
  getEngagementTimeline, 
  getVerticalPerformance,
  getChatSessionsAnalytics
} from "@/lib/api";
import { 
  Users, 
  TrendingUp, 
  MessageSquare, 
  Target, 
  Clock, 
  BarChart3,
  PieChart,
  Activity,
  UserCheck,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Loader2,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

// Mini Chart Component
const MiniBarChart = ({ data, maxValue, color = "#0066CC" }) => {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-12">
      {data.map((d, i) => (
        <div 
          key={i} 
          className="flex-1 rounded-t transition-all duration-300 hover:opacity-80"
          style={{ 
            height: `${(d.value / max) * 100}%`,
            backgroundColor: color,
            minHeight: d.value > 0 ? '4px' : '0'
          }}
          title={`${d.label}: ${d.value}`}
        />
      ))}
    </div>
  );
};

// Funnel Stage Component
const FunnelStage = ({ label, count, percentage, color, isLast = false }) => (
  <div className="relative">
    <div 
      className="py-4 px-6 rounded-lg text-white text-center transition-transform hover:scale-105"
      style={{ 
        backgroundColor: color,
        width: `${Math.max(percentage, 30)}%`,
        marginLeft: `${(100 - Math.max(percentage, 30)) / 2}%`
      }}
    >
      <div className="text-2xl font-bold">{count}</div>
      <div className="text-sm opacity-90">{label}</div>
    </div>
    {!isLast && (
      <div className="flex justify-center my-1">
        <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent" 
             style={{ borderTopColor: color }} />
      </div>
    )}
  </div>
);

export default function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [leadFunnel, setLeadFunnel] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [verticalPerformance, setVerticalPerformance] = useState(null);
  const [chatAnalytics, setChatAnalytics] = useState(null);
  const [timelineDays, setTimelineDays] = useState("7");

  const fetchAllData = async (showToast = false) => {
    try {
      setRefreshing(true);
      const [summaryRes, funnelRes, timelineRes, verticalRes, chatRes] = await Promise.all([
        getAnalyticsSummary(),
        getLeadFunnel(),
        getEngagementTimeline(parseInt(timelineDays)),
        getVerticalPerformance(),
        getChatSessionsAnalytics()
      ]);
      
      setSummary(summaryRes.data);
      setLeadFunnel(funnelRes.data);
      setTimeline(timelineRes.data);
      setVerticalPerformance(verticalRes.data);
      setChatAnalytics(chatRes.data);
      
      if (showToast) toast.success("Analytics refreshed");
    } catch (error) {
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineDays]);

  const handleRefresh = () => fetchAllData(true);

  if (loading) {
    return (
      <AdminLayout title="Analytics Dashboard">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-[#0066CC]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Analytics Dashboard">
      <div className="space-y-6" data-testid="admin-analytics">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Management Analytics</h2>
            <p className="text-slate-500">User behavior patterns, lead funnels, and engagement metrics</p>
          </div>
          <Button onClick={handleRefresh} variant="outline" disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-3xl font-bold text-slate-900">{summary?.total_users || 0}</p>
                  <p className="text-xs text-emerald-600 flex items-center mt-1">
                    <UserCheck className="h-3 w-3 mr-1" />
                    {summary?.active_users_today || 0} active today
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Sessions</p>
                  <p className="text-3xl font-bold text-slate-900">{summary?.total_sessions || 0}</p>
                  <p className="text-xs text-slate-500 flex items-center mt-1">
                    <Clock className="h-3 w-3 mr-1" />
                    Avg: {Math.round(summary?.avg_session_duration || 0)}s
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">AI Queries</p>
                  <p className="text-3xl font-bold text-slate-900">{summary?.total_ai_queries || 0}</p>
                  <p className="text-xs text-amber-600 flex items-center mt-1">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    {chatAnalytics?.total_sessions || 0} chat sessions
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Conversion Rate</p>
                  <p className="text-3xl font-bold text-slate-900">{summary?.conversion_rate || 0}%</p>
                  <p className="text-xs text-emerald-600 flex items-center mt-1">
                    <Target className="h-3 w-3 mr-1" />
                    {leadFunnel?.converted || 0} converted
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Engagement Timeline */}
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Engagement Timeline</CardTitle>
                  <CardDescription>Daily activity over time</CardDescription>
                </div>
                <Select value={timelineDays} onValueChange={setTimelineDays}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {timeline?.timeline && (
                <div className="space-y-4">
                  {/* Chart Header */}
                  <div className="flex items-center gap-6 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-blue-500" /> Page Views
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-purple-500" /> Unique Users
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-amber-500" /> AI Queries
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-emerald-500" /> Leads
                    </span>
                  </div>
                  
                  {/* Charts */}
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Page Views</p>
                      <MiniBarChart 
                        data={timeline.timeline.map(d => ({ label: d.day_name, value: d.page_views }))}
                        color="#3B82F6"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Unique Users</p>
                      <MiniBarChart 
                        data={timeline.timeline.map(d => ({ label: d.day_name, value: d.unique_users }))}
                        color="#8B5CF6"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-2">AI Queries</p>
                      <MiniBarChart 
                        data={timeline.timeline.map(d => ({ label: d.day_name, value: d.ai_queries }))}
                        color="#F59E0B"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Leads</p>
                      <MiniBarChart 
                        data={timeline.timeline.map(d => ({ label: d.day_name, value: d.leads_captured }))}
                        color="#10B981"
                      />
                    </div>
                  </div>
                  
                  {/* Day Labels */}
                  <div className="flex justify-between text-xs text-slate-400 px-1">
                    {timeline.timeline.map((d, i) => (
                      <span key={i}>{d.day_name}</span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lead Funnel */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Lead Funnel</CardTitle>
              <CardDescription>Conversion stages</CardDescription>
            </CardHeader>
            <CardContent>
              {leadFunnel && (
                <div className="space-y-2">
                  <FunnelStage 
                    label="New" 
                    count={leadFunnel.new_leads} 
                    percentage={100}
                    color="#3B82F6"
                  />
                  <FunnelStage 
                    label="Contacted" 
                    count={leadFunnel.contacted} 
                    percentage={leadFunnel.total_leads > 0 ? (leadFunnel.contacted / leadFunnel.total_leads) * 100 : 0}
                    color="#8B5CF6"
                  />
                  <FunnelStage 
                    label="Qualified" 
                    count={leadFunnel.qualified} 
                    percentage={leadFunnel.total_leads > 0 ? (leadFunnel.qualified / leadFunnel.total_leads) * 100 : 0}
                    color="#F59E0B"
                  />
                  <FunnelStage 
                    label="Converted" 
                    count={leadFunnel.converted} 
                    percentage={leadFunnel.total_leads > 0 ? (leadFunnel.converted / leadFunnel.total_leads) * 100 : 0}
                    color="#10B981"
                    isLast
                  />
                  
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Conversion Rate</span>
                      <span className="font-semibold text-emerald-600">{leadFunnel.conversion_rate}%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-slate-500">Lost</span>
                      <span className="font-semibold text-red-500">{leadFunnel.lost}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vertical Performance */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#0066CC]" />
                Vertical Performance
              </CardTitle>
              <CardDescription>Industry-wise engagement metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {verticalPerformance?.verticals?.slice(0, 6).map((v, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{v.vertical_name}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                        <span>{v.total_views} views</span>
                        <span>{v.unique_visitors} visitors</span>
                        <span>{v.leads_generated} leads</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${v.conversion_rate > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {v.conversion_rate}%
                      </div>
                      <div className="text-xs text-slate-400">conv.</div>
                    </div>
                  </div>
                ))}
                {(!verticalPerformance?.verticals || verticalPerformance.verticals.length === 0) && (
                  <p className="text-center text-slate-400 py-8">No vertical data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* User Engagement & AI Insights */}
          <div className="space-y-6">
            {/* User Engagement Distribution */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-purple-500" />
                  User Engagement
                </CardTitle>
                <CardDescription>Distribution by engagement level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(summary?.user_engagement_distribution || {}).map(([level, count]) => (
                    <div 
                      key={level} 
                      className={`text-center p-3 rounded-lg ${
                        level === 'Very High' ? 'bg-emerald-100 text-emerald-700' :
                        level === 'High' ? 'bg-blue-100 text-blue-700' :
                        level === 'Medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}
                    >
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-xs">{level}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* AI Chat Insights */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-amber-500" />
                  AI Chat Insights
                </CardTitle>
                <CardDescription>User interests from AI conversations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Total Chat Sessions</span>
                    <span className="font-semibold">{chatAnalytics?.total_sessions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Total Messages</span>
                    <span className="font-semibold">{chatAnalytics?.total_messages || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Avg Messages/Session</span>
                    <span className="font-semibold">{chatAnalytics?.avg_messages_per_session || 0}</span>
                  </div>
                  
                  {chatAnalytics?.top_interests?.length > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Top User Interests</p>
                      <div className="flex flex-wrap gap-2">
                        {chatAnalytics.top_interests.slice(0, 6).map((interest, i) => (
                          <span 
                            key={i}
                            className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-full"
                          >
                            {interest.topic} ({interest.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Top Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Verticals */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Top Verticals</CardTitle>
              <CardDescription>Most viewed industries</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {summary?.top_verticals?.map((v, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{v.name}</p>
                    </div>
                    <div className="text-sm text-slate-500">{v.views} views</div>
                  </div>
                ))}
                {(!summary?.top_verticals || summary.top_verticals.length === 0) && (
                  <p className="text-center text-slate-400 py-4">No data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Problems */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Top Problem Statements</CardTitle>
              <CardDescription>Most clicked challenges</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {summary?.top_problems?.map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{p.title}</p>
                    </div>
                    <div className="text-sm text-slate-500">{p.clicks} clicks</div>
                  </div>
                ))}
                {(!summary?.top_problems || summary.top_problems.length === 0) && (
                  <p className="text-center text-slate-400 py-4">No data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
